/**
 * Experiment exposure tracking for Mixpanel.
 *
 * Fires Mixpanel's native `$experiment_started` exposure event so the Mixpanel
 * Experiments report can attribute downstream behaviour to the right variant.
 * Event/property names match Mixpanel's defaults ("Experiment name" / "Variant
 * name") so the report works with no project-side configuration.
 *
 * Two entry points:
 *   1. window.trackExperimentExposure({ experiment, variant, source, props })
 *      Generic emitter — used by the Algolia hook below and by any custom /
 *      Option-B (pre-paint client-side bucketing) theme test.
 *   2. Algolia A/B test auto-exposure — reads abTestID / abTestVariantID off the
 *      InstantSearch results (search AND collection browse, same products index)
 *      and forwards exposure.
 *
 * IMPORTANT: theme/component tests served by the Mixpanel Feature Flags SDK
 * (Option A) are auto-exposed by the SDK when `mixpanel.flags.get_variant_value()`
 * is evaluated — those tests must NOT also call window.trackExperimentExposure,
 * or exposure would be double-counted. This module is for Algolia + custom tests.
 *
 * Consent: Mixpanel only loads behind OneTrust C0002, so exposure events only
 * fire for consented users (variant assignment itself is never gated). If Mixpanel
 * is not present at exposure time we no-op, matching the other *-tracking.js files.
 */
(function () {
  'use strict';

  // Dedupe. Mixpanel wants exposure on *actual* exposure, once — not on every
  // event and not merely on assignment. Fire at most once per session per
  // experiment+variant. Once-per-session (not once-ever) keeps the 90-day
  // attribution window alive for returning visitors without spamming.
  var sentThisSession = new Set();
  var SESSION_PREFIX = 'mp_exp_exposed::';

  function alreadySent(key) {
    if (sentThisSession.has(key)) return true;
    try {
      if (sessionStorage.getItem(SESSION_PREFIX + key) === '1') {
        sentThisSession.add(key);
        return true;
      }
    } catch (e) {
      // sessionStorage unavailable (private mode) — in-memory dedupe still applies
    }
    return false;
  }

  function markSent(key) {
    sentThisSession.add(key);
    try {
      sessionStorage.setItem(SESSION_PREFIX + key, '1');
    } catch (e) {
      // ignore
    }
  }

  function resolvePageType() {
    var t = (window.Shopify && window.Shopify.template_name) || '';
    var map = { index: 'homepage', product: 'pdp', collection: 'category', search: 'search', cart: 'cart' };
    if (t) {
      // template.name can be e.g. "product.alternate" — key off the base.
      var base = String(t).split('.')[0];
      return map[base] || base || 'other';
    }
    return 'other';
  }

  /**
   * Generic exposure emitter.
   * @param {{experiment: string, variant: (string|number), source?: string, props?: object}} opts
   * @returns {boolean} true if an event was sent
   */
  function trackExperimentExposure(opts) {
    try {
      if (!opts || !opts.experiment) return false;
      if (opts.variant === undefined || opts.variant === null || opts.variant === '') return false;

      // Mixpanel present? (consent granted + SDK loaded). No-op otherwise.
      if (typeof window.mixpanel === 'undefined' || !window.mixpanel.track) {
        return false;
      }

      // Skip bots, like every other emitter.
      if (typeof isBot === 'function' && isBot()) {
        return false;
      }

      var experiment = String(opts.experiment);
      var variant = String(opts.variant);
      // QA-forced exposures are tagged so they can be filtered out of the report, so they must not
      // occupy the dedupe slot of the tester's REAL arm. Both the override and this dedupe live in
      // sessionStorage for the same tab: force `remove_all`, then clear with `?_exp_<key>=off` and
      // land on `remove_all` for real, and without the suffix the genuine exposure is swallowed —
      // leaving that session with no countable exposure at all once QA rows are excluded.
      var isQaOverride = !!(opts.props && opts.props['QA Override']);
      var key = experiment + '::' + variant + (isQaOverride ? '::qa' : '');

      if (alreadySent(key)) return false;

      var payload = {
        // Mixpanel Experiments report defaults — keep these names exact.
        'Experiment name': experiment,
        'Variant name': variant,
        // Context. Super-properties (Display Language / Market / Country /
        // Is Logged In / Customer Type) attach automatically.
        Source: opts.source || 'unknown',
        'Page Type': resolvePageType(),
        'Current URL': window.location.href,
        'User Agent': navigator.userAgent,
      };

      if (opts.props && typeof opts.props === 'object') {
        Object.keys(opts.props).forEach(function (k) {
          if (!(k in payload)) payload[k] = opts.props[k];
        });
      }

      mixpanel.track('$experiment_started', payload);
      markSent(key);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Expose for the Algolia hook + custom / Option-B theme tests.
  window.trackExperimentExposure = trackExperimentExposure;

  // ---------------------------------------------------------------------------
  // Exposure-on-view (Option-B / custom theme tests)
  // ---------------------------------------------------------------------------
  // Fires exposure when the variant surface is actually SEEN (not on assignment),
  // matching Mixpanel's "send exposure, not assignment" rule. Assignment happens
  // pre-paint/pre-consent (see snippets/experiment-bucketing.liquid); this waits
  // for Mixpanel (i.e. for consent) up to a bound, so a user who consents shortly
  // after seeing the surface is still recorded. Flag-driven Option-A tests must
  // NOT use this — the Feature Flags SDK auto-exposes on evaluation.

  function whenMixpanelReady(cb, maxWaitMs) {
    var waited = 0;
    var step = 1000;
    (function check() {
      if (typeof window.mixpanel !== 'undefined' && window.mixpanel.track) {
        cb();
        return;
      }
      waited += step;
      if (waited >= (maxWaitMs || 20000)) return; // give up — user never consented in window
      setTimeout(check, step);
    })();
  }

  // Fire `cb` once, when `el` first intersects the viewport. threshold 0 = any pixel
  // (0.5 can never be met by elements taller than ~2x the viewport); rootMargin lets
  // the caller act just before the surface is seen. No IntersectionObserver / no el →
  // fire immediately. Shared by the exposure and flag engines.
  // Observers still waiting on a first intersection. An IntersectionObserver keeps a strong
  // reference to its target, so one whose element is swapped out before it is ever seen would
  // retain that element — and, through parentNode, the whole detached subtree it came from.
  // pruneDetachedObservers() releases those; see the re-arm block at the bottom of the file.
  var pendingObservers = [];

  function pruneDetachedObservers() {
    for (var i = pendingObservers.length - 1; i >= 0; i--) {
      var entry = pendingObservers[i];
      if (entry.el && entry.el.isConnected === false) {
        entry.io.disconnect();
        pendingObservers.splice(i, 1);
      }
    }
  }

  function forgetObserver(io) {
    for (var i = 0; i < pendingObservers.length; i++) {
      if (pendingObservers[i].io === io) {
        pendingObservers.splice(i, 1);
        return;
      }
    }
  }

  function onceVisible(el, opts, cb) {
    opts = opts || {};
    if (el && typeof window.IntersectionObserver === 'function') {
      var io = new window.IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (entries[i].isIntersecting) {
              io.disconnect();
              forgetObserver(io);
              cb();
              break;
            }
          }
        },
        { threshold: opts.threshold != null ? opts.threshold : 0, rootMargin: opts.rootMargin || '0px' },
      );
      io.observe(el);
      pendingObservers.push({ el: el, io: io });
    } else {
      cb();
    }
  }

  function experimentExposeOnView(el, opts) {
    if (!el || !opts) return;
    var fired = false;
    onceVisible(el, { threshold: 0 }, function () {
      if (fired) return;
      fired = true;
      whenMixpanelReady(function () {
        trackExperimentExposure(opts);
      });
    });
  }

  window.experimentExposeOnView = experimentExposeOnView;

  // ---------------------------------------------------------------------------
  // Flag-driven variant (Option A) — for below-the-fold tests
  // ---------------------------------------------------------------------------
  // Resolves a Mixpanel feature flag when `el` nears the viewport (rootMargin lead,
  // so the variant is applied before the section is actually seen — no on-arrival
  // shift), then calls onVariant(variant). Evaluating the flag AUTO-FIRES the
  // $experiment_started exposure event, so this is for flag-driven tests only — do
  // NOT also call trackExperimentExposure for the same test. Above-the-fold tests
  // should use pre-paint (window.Experiments) instead, since the flag is async.
  function experimentFlagVariant(el, flag, onVariant, opts) {
    if (!flag || typeof onVariant !== 'function') return;
    opts = opts || {};
    var fallback = opts.fallback != null ? opts.fallback : 'control';
    var resolved = false;

    function resolve() {
      if (resolved) return;
      resolved = true;
      var waited = 0;
      (function check() {
        var mp = window.mixpanel;
        if (mp && mp.flags && typeof mp.flags.get_variant_value === 'function') {
          mp.flags
            .get_variant_value(flag, fallback)
            .then(function (v) {
              try {
                onVariant(v);
              } catch (e) {
                /* caller error */
              }
            })
            .catch(function () {
              try {
                onVariant(fallback);
              } catch (e) {
                /* caller error */
              }
            });
          return;
        }
        waited += 200;
        if (waited >= (opts.maxWaitMs || 20000)) {
          try {
            onVariant(fallback); // Mixpanel never loaded → apply fallback (no exposure fired), symmetric with .catch
          } catch (e) {
            /* caller error */
          }
          return;
        }
        setTimeout(check, 200);
      })();
    }

    onceVisible(el, { rootMargin: opts.rootMargin || '400px 0px' }, resolve);
  }

  window.experimentFlagVariant = experimentFlagVariant;

  // ---------------------------------------------------------------------------
  // Declarative auto-init — wire experiments from data- attributes so sections
  // don't hand-roll inline init scripts.
  // ---------------------------------------------------------------------------
  //   Pre-paint exposure (Option B): on the surface element —
  //     data-exp-expose="<Experiment name>" data-exp-key="<assignment slug>" [data-exp-source]
  //     Variant is read from window.Experiments.get(key); $experiment_started fires on view.
  //   Flag-driven (Option A): on the container element —
  //     data-exp-flag="<mixpanel flag key>" [data-exp-root-margin]
  //     Descendants tagged data-exp-variant="control|treatment|all" are shown/hidden for the
  //     resolved variant (control = default rendered; treatment hidden until resolved). The
  //     flag eval auto-fires exposure.
  function applyVariantVisibility(root, variant) {
    var v = typeof variant === 'string' ? variant.trim().toLowerCase() : variant;
    if (v !== 'treatment') return; // control = the default already rendered
    var hide = root.querySelectorAll('[data-exp-variant="control"]');
    for (var i = 0; i < hide.length; i++) hide[i].classList.add('exp-hidden');
    var show = root.querySelectorAll('[data-exp-variant="treatment"]');
    for (var j = 0; j < show.length; j++) show[j].classList.remove('exp-hidden');
    // If this container is (or sits inside) a Dawn slider-component, re-init it — it was
    // set up against the pre-swap slide set and recomputes via a ResizeObserver on its own
    // box, which a class toggle on flex children doesn't trigger. Scoped to the actual
    // slider so we don't fire a global resize for plain (non-carousel) grids.
    try {
      var slider = (root.closest && root.closest('slider-component')) || (root.querySelector && root.querySelector('slider-component'));
      if (slider && typeof slider.reinit === 'function') slider.reinit();
    } catch (e) {
      /* no-op */
    }
  }

  // `:not([data-exp-wired])` + marking each element makes re-runs idempotent
  // (shopify:section:load re-renders, dynamic injection) — no double wiring/exposure.
  // `:not([data-exp-unresolved])` skips surfaces parked by parkUnresolvedSurfaces() below: they
  // can never resolve, so re-walking them on every pass is pure waste on a busy PDP.
  var UNWIRED_EXPOSE = '[data-exp-expose]:not([data-exp-wired]):not([data-exp-unresolved])';
  var UNWIRED_FLAG = '[data-exp-flag]:not([data-exp-wired]):not([data-exp-unresolved])';
  var UNWIRED = UNWIRED_EXPOSE + ',' + UNWIRED_FLAG;
  // Every surface, regardless of wiring state — a "does this page carry an experiment at all?"
  // probe, used by shouldObserve() below.
  var ANY_SURFACE = '[data-exp-expose],[data-exp-flag]';

  function autoInitExperiments() {
    if (!document.querySelectorAll) return;

    // Option B — pre-paint exposure surfaces.
    var exposeEls = document.querySelectorAll(UNWIRED_EXPOSE);
    for (var i = 0; i < exposeEls.length; i++) {
      (function (el) {
        if (!window.Experiments) return;
        var key = el.getAttribute('data-exp-key');
        var variant = window.Experiments.get(key);
        if (!variant) return;
        el.setAttribute('data-exp-wired', '');
        // A variant forced via ?_exp_<key>=<variant> is QA traffic, not a real bucketing
        // decision. Stamp it so those exposures can be excluded in Mixpanel rather than
        // quietly counting a tester as a participant.
        var isOverride =
          typeof window.Experiments.isOverride === 'function' && window.Experiments.isOverride(key);
        experimentExposeOnView(el, {
          experiment: el.getAttribute('data-exp-expose'),
          variant: variant,
          source: el.getAttribute('data-exp-source') || 'shopify',
          props: isOverride ? { 'QA Override': true } : null,
        });
      })(exposeEls[i]);
    }

    // Option A — flag-driven variant visibility.
    var flagEls = document.querySelectorAll(UNWIRED_FLAG);
    for (var k = 0; k < flagEls.length; k++) {
      (function (el) {
        el.setAttribute('data-exp-wired', '');
        experimentFlagVariant(
          el,
          el.getAttribute('data-exp-flag'),
          function (variant) {
            applyVariantVisibility(el, variant);
          },
          { rootMargin: el.getAttribute('data-exp-root-margin') || '400px 0px' },
        );
      })(flagEls[k]);
    }
  }

  // Re-arm for in-place DOM swaps. product-info.js `handleSwapProduct` replaces <main> /
  // <product-info> wholesale (HTMLUpdateUtility.viewTransition) on linked-product and
  // combined-listing changes — which drops `data-exp-wired` and fires neither DOMContentLoaded
  // nor shopify:section:load, so the swapped-in surface would never be wired and its exposure
  // would be lost silently. The observer callback is a bare flag check so it stays cheap on a
  // busy PDP; the actual work is debounced and short-circuits on the querySelector when there is
  // nothing new. `data-exp-wired` keeps re-runs idempotent and trackExperimentExposure's
  // once-per-session dedupe prevents double-counting. (UNWIRED is declared next to
  // autoInitExperiments above, since both passes key off the same selectors.)

  // autoInitExperiments deliberately leaves an element unmarked when it can't resolve a variant
  // (no window.Experiments, or no assignment for data-exp-key). Without this, one such element —
  // e.g. a data-exp-expose host whose assign() never ran — would match UNWIRED forever and turn
  // every mutation batch on a busy PDP into a document-wide rescan for the life of the page.
  // Pre-paint assignment is final before DOMContentLoaded, so anything still unresolved after a
  // pass never will be; park it. A later re-render produces fresh nodes without the marker.
  function parkUnresolvedSurfaces() {
    // Only park once the bucketing engine is actually up. Without this guard, a page where
    // window.Experiments never got defined (blocked inline script, CSP, snippet not rendered on
    // that layout) parks EVERY expose surface on the first pass — and nothing ever clears
    // `data-exp-unresolved`, so a later-arriving engine can no longer rescue them. "Unresolvable"
    // is only a safe conclusion when the thing that resolves them exists and still said no.
    if (!window.Experiments) return;
    var stale = document.querySelectorAll(UNWIRED);
    for (var i = 0; i < stale.length; i++) stale[i].setAttribute('data-exp-unresolved', '');
  }

  // Scope the observer. It only re-arms surfaces injected AFTER load — the sole case in this theme
  // is product-info.js `handleSwapProduct`, which replaces <main>/<product-info> on linked-product
  // and combined-listing changes. That is PDP-only. Everywhere else an experiment surface is either
  // present at load (wired by the initial pass) or re-rendered via shopify:section:load (wired by
  // that listener) — neither needs this watcher. Running it globally would observe /search and
  // collection browse, where InstantSearch re-renders the whole product grid on every keystroke: a
  // document-wide selector check every 100ms, forever, on pages with no such surface. So it is
  // limited to product pages. This does NOT affect which pages run experiments or fire exposure —
  // only where this after-load re-check runs.
  function shouldObserve() {
    var pageType = resolvePageType();
    if (pageType === 'pdp') return true;
    // 'other' means window.Shopify.template_name was missing or is a type this map doesn't know
    // (another script replaced window.Shopify, a layout that doesn't set it, this asset reused
    // elsewhere). We cannot tell whether that is a PDP, and failing closed would silently kill the
    // re-arm on a real one — swapped-in surfaces would stop exposing with no error and no signal.
    // Fail open only when the page actually carries a surface: search and collection browse resolve
    // to known types, so the pages this scoping exists to protect are still skipped either way.
    return pageType === 'other' && !!document.querySelector(ANY_SURFACE);
  }

  function observeForUnwiredSurfaces() {
    if (typeof window.MutationObserver !== 'function' || !document.body || !document.querySelector) return;
    if (!shouldObserve()) return;

    var rearmPending = false;
    var observer = new window.MutationObserver(function () {
      if (rearmPending) return;
      rearmPending = true;
      // setTimeout, not requestAnimationFrame — rAF is starved in background tabs, and a swap
      // can complete while the tab is hidden.
      setTimeout(function () {
        rearmPending = false;
        pruneDetachedObservers();
        if (!document.querySelector(UNWIRED)) return;
        wirePass();
      }, 100);
    });

    observer.observe(document.body, { childList: true, subtree: true });
  }

  // One wiring pass = wire what can be wired, then park what still can't. Parking has to follow
  // EVERY pass, not just the mutation-driven one: this script is deferred, so by the time any pass
  // runs the inline assign() of every parsed section has already executed, and a surface the live
  // engine still can't resolve never will. Leaving the initial pass unparked meant the first
  // mutation batch on any page carrying such a surface — e.g. a `data-exp-key` with no matching
  // assign(), which is what a section whose inline init drifted out of step with its markup emits —
  // paid a full document-wide re-scan.
  function wirePass() {
    autoInitExperiments();
    parkUnresolvedSurfaces();
  }

  function bootstrap() {
    wirePass();
    observeForUnwiredSurfaces();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
  // Re-arm for Shopify theme-editor section re-renders + dynamically injected sections.
  // A re-render produces fresh nodes without either marker, so parking never strands them.
  // Prune first: the re-render detached the old section's nodes, and off the PDP this is the only
  // place that runs — the swap observer above is scoped to product pages, so without this the
  // detached-target release would never happen on a homepage/collection section re-render.
  document.addEventListener('shopify:section:load', function () {
    pruneDetachedObservers();
    wirePass();
  });

  // ---------------------------------------------------------------------------
  // Algolia A/B test auto-exposure
  // ---------------------------------------------------------------------------
  // Algolia assigns variants server-side (sticky per userToken) and returns
  // abTestID / abTestVariantID on the search response. The SAME InstantSearch
  // products index powers both /search and collection browse, so a ranking test
  // exposes browsing users too — we hook the helper `result` event (fires on
  // both surfaces) rather than gating on the /search path.

  var algoliaAttached = false; // 'result' listener attached
  var algoliaPolling = false;

  // Optional human labels for Algolia tests, keyed by numeric abTestID. Set from
  // a theme snippet/settings if desired, e.g.
  //   window.ALGOLIA_EXPERIMENT_LABELS = { '42': 'Search Ranking — Brand Boost' };
  // Falls back to a stable generated name (no code change when tests rotate).
  function algoliaExperimentName(abTestID) {
    var labels = window.ALGOLIA_EXPERIMENT_LABELS || {};
    return labels[String(abTestID)] || 'Algolia AB Test ' + abTestID;
  }

  // abTestVariantID is 1-based; map to letters for readability (1 -> A, 2 -> B …).
  function variantLabel(variantId) {
    var n = parseInt(variantId, 10);
    if (n >= 1 && n <= 26) return String.fromCharCode(64 + n);
    return 'variant-' + variantId;
  }

  function readAlgoliaABTest() {
    try {
      var search =
        window.algoliaShopify && window.algoliaShopify.instantsearch && window.algoliaShopify.instantsearch.search;
      if (!search || !search.helper) return null;

      var results = search.helper.lastResults;
      if (!results) return null;

      var abTestID = results.abTestID;
      var abTestVariantID = results.abTestVariantID;

      // Fallback to the raw response if the helper doesn't surface them directly.
      if ((abTestID === undefined || abTestVariantID === undefined) && results._rawResults && results._rawResults[0]) {
        var raw = results._rawResults[0];
        if (abTestID === undefined) abTestID = raw.abTestID;
        if (abTestVariantID === undefined) abTestVariantID = raw.abTestVariantID;
      }

      if (abTestID === undefined || abTestVariantID === undefined || abTestVariantID === null) {
        return null; // no A/B test running on this query
      }

      return { abTestID: abTestID, abTestVariantID: abTestVariantID };
    } catch (e) {
      return null;
    }
  }

  var algoliaWaitPending = false;

  function sendAlgoliaExposure(ab) {
    trackExperimentExposure({
      experiment: algoliaExperimentName(ab.abTestID),
      variant: variantLabel(ab.abTestVariantID),
      source: 'algolia',
      props: {
        'Algolia AB Test ID': ab.abTestID,
        'Algolia Variant ID': ab.abTestVariantID,
      },
    });
  }

  function fireAlgoliaExposure() {
    var ab = readAlgoliaABTest();
    if (!ab) return;
    // Mixpanel is consent-gated and may not be loaded at the first/only `result`
    // event. Wait for it (bounded) so a user who consents shortly after the search
    // is still recorded — rather than silently relying on another result event to
    // re-fire. Dedupe in trackExperimentExposure keeps it to one event.
    if (typeof window.mixpanel !== 'undefined' && window.mixpanel.track) {
      sendAlgoliaExposure(ab);
      return;
    }
    if (algoliaWaitPending) return;
    algoliaWaitPending = true;
    whenMixpanelReady(function () {
      algoliaWaitPending = false;
      var latest = readAlgoliaABTest();
      if (latest) sendAlgoliaExposure(latest);
    });
  }

  function initAlgoliaExposure() {
    if (algoliaAttached || algoliaPolling) return;
    algoliaPolling = true;

    var attempts = 0;
    var poll = setInterval(function () {
      attempts++;
      var search =
        window.algoliaShopify && window.algoliaShopify.instantsearch && window.algoliaShopify.instantsearch.search;

      if (search && search.helper) {
        clearInterval(poll);
        algoliaPolling = false;
        if (algoliaAttached) return;
        algoliaAttached = true;

        // Catch a test already resolved before we attached (initial render).
        fireAlgoliaExposure();

        // Fire on each subsequent results render; dedupe keeps it once/session.
        search.helper.on('result', fireAlgoliaExposure);
        return;
      }

      if (attempts >= 100) {
        // ~10s; give up — re-armed by algolia.finishedInitialization below.
        clearInterval(poll);
        algoliaPolling = false;
      }
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlgoliaExposure);
  } else {
    initAlgoliaExposure();
  }

  // Re-arm if Algolia signals (re)initialization, matching the other emitters.
  window.addEventListener('algolia.finishedInitialization', function () {
    if (!algoliaAttached) initAlgoliaExposure();
  });
})();
